/**
 * Seer Output Formatters
 *
 * Formatting utilities for Seer Autofix command output. All human-readable
 * output is built as markdown and rendered via renderMarkdown().
 */

import type {
  AutofixState,
  RootCause,
  SolutionArtifact,
} from "../../types/seer.js";
import { ApiError, SeerError } from "../errors.js";
import { cyan } from "./colors.js";
import { escapeMarkdownInline, renderMarkdown } from "./markdown.js";

// Spinner Frames

const SPINNER_FRAMES = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];

/**
 * Get a spinner frame for the given tick count.
 *
 * @param tick - Current animation tick (cycles through frames)
 * @returns Single spinner character for display
 */
export function getSpinnerFrame(tick: number): string {
  const index = tick % SPINNER_FRAMES.length;
  // biome-ignore lint/style/noNonNullAssertion: index is always valid due to modulo
  return SPINNER_FRAMES[index]!;
}

// Progress Formatting

/** Maximum length for progress messages to fit in a single terminal line */
const MAX_PROGRESS_LENGTH = 300;

/**
 * Truncate a progress message to fit in a single terminal line.
 *
 * @param message - Progress message to truncate
 * @returns Truncated message with ellipsis if needed
 */
export function truncateProgressMessage(message: string): string {
  if (message.length <= MAX_PROGRESS_LENGTH) {
    return message;
  }
  return `${message.slice(0, MAX_PROGRESS_LENGTH - 3)}...`;
}

/**
 * Format a progress message with spinner.
 *
 * @param message - Progress message to display
 * @param tick - Spinner tick count
 * @returns Formatted progress line
 */
export function formatProgressLine(message: string, tick: number): string {
  const spinner = cyan(getSpinnerFrame(tick));
  return `${spinner} ${message}`;
}

/**
 * Extract the latest progress message from autofix state.
 *
 * @param state - Current autofix state
 * @returns Latest progress message or default
 */
export function getProgressMessage(state: AutofixState): string {
  if (!state.steps) {
    return "Processing...";
  }

  // Find the most recent progress message
  for (let i = state.steps.length - 1; i >= 0; i--) {
    const step = state.steps[i];
    if (step?.progress && step.progress.length > 0) {
      const lastProgress = step.progress.at(-1);
      if (lastProgress?.message) {
        return lastProgress.message;
      }
    }
  }

  // Fallback based on status
  switch (state.status) {
    case "PROCESSING":
      return "Analyzing issue...";
    case "COMPLETED":
      return "Analysis complete";
    case "ERROR":
      return "Analysis failed";
    default:
      return "Processing...";
  }
}

// Root Cause Formatting

/**
 * Build a markdown document for a single root cause.
 *
 * @param cause - Root cause to format
 * @param index - Index for display (used as cause ID)
 * @returns Markdown string for this cause
 */
function buildRootCauseMarkdown(cause: RootCause, index: number): string {
  const lines: string[] = [];

  lines.push(
    `### Cause #${index}: ${escapeMarkdownInline(cause.description ?? "")}`
  );
  lines.push("");

  if (cause.relevant_repos && cause.relevant_repos.length > 0) {
    lines.push(`**Repository:** ${cause.relevant_repos.join(", ")}`);
    lines.push("");
  }

  if (
    cause.root_cause_reproduction &&
    cause.root_cause_reproduction.length > 0
  ) {
    lines.push("**Reproduction steps:**");
    lines.push("");
    for (const step of cause.root_cause_reproduction) {
      lines.push(`**${escapeMarkdownInline(step.title)}**`);
      lines.push("");
      // code_snippet_and_analysis may itself contain markdown (code fences,
      // inline code, etc.) — pass it through as-is so marked renders it.
      lines.push(step.code_snippet_and_analysis);
      lines.push("");
    }
  }

  return lines.join("\n");
}

/**
 * Format all root causes as rendered terminal output.
 *
 * @param causes - Array of root causes
 * @returns Rendered terminal string
 */
export function formatRootCauseList(causes: RootCause[]): string {
  const lines: string[] = [];

  lines.push("## Root Cause Analysis Complete");
  lines.push("");

  if (causes.length === 0) {
    lines.push("*No root causes identified.*");
  } else {
    for (let i = 0; i < causes.length; i++) {
      const cause = causes[i];
      if (cause) {
        lines.push(buildRootCauseMarkdown(cause, i));
      }
    }
  }

  return renderMarkdown(lines.join("\n"));
}

// Error Messages

/**
 * Create a SeerError from an API error status code and detail.
 *
 * @param status - HTTP status code
 * @param detail - Error detail from API
 * @param orgSlug - Organization slug for constructing settings URLs
 * @returns SeerError if the status code indicates a Seer-specific error, null otherwise
 */
export function createSeerError(
  status: number,
  detail?: string,
  orgSlug?: string
): SeerError | null {
  if (status === 402) {
    return new SeerError("no_budget", orgSlug);
  }
  if (status === 403) {
    if (detail?.includes("not enabled")) {
      return new SeerError("not_enabled", orgSlug);
    }
    if (detail?.includes("AI features")) {
      return new SeerError("ai_disabled", orgSlug);
    }
    // Unrecognized 403 - return null to preserve original error detail
    // (could be permission denied, rate limiting, etc.)
    return null;
  }
  return null;
}

/**
 * Convert an API error to a Seer-specific error or an ApiError.
 *
 * Returns a SeerError for known Seer issues (402/403 with specific detail),
 * or preserves the error as an ApiError for other failures. Previously
 * returned a plain Error which lost the status code and endpoint — causing
 * CLI-N (84 users) where unrecognized 403s became untyped errors.
 *
 * @param status - HTTP status code
 * @param detail - Error detail from API
 * @param orgSlug - Organization slug for constructing settings URLs
 * @returns SeerError for Seer-specific errors, or ApiError for other API errors
 */
export function handleSeerApiError(
  status: number,
  detail?: string,
  orgSlug?: string
): Error {
  const seerError = createSeerError(status, detail, orgSlug);
  if (seerError) {
    return seerError;
  }
  return new ApiError(formatAutofixError(status, detail), status, detail);
}

/**
 * Format an error message for non-Seer autofix errors.
 *
 * Note: Seer-specific errors (402, 403) are handled by SeerError which
 * provides actionable suggestions. This function handles other API errors.
 *
 * @param status - HTTP status code
 * @param detail - Error detail from API
 * @returns User-friendly error message
 */
export function formatAutofixError(status: number, detail?: string): string {
  switch (status) {
    case 404:
      return "Issue not found.";
    default:
      return detail ?? "An error occurred with the autofix request.";
  }
}

// Solution Formatting

/**
 * Format a solution artifact as rendered terminal output.
 *
 * Renders a markdown document:
 *
 * ## Solution
 *
 * **Summary:** {one_line_summary}
 *
 * ### Steps to implement
 *
 * 1. **{title}**
 *
 *    {description}
 *
 * @param solution - Solution artifact from autofix
 * @returns Rendered terminal string
 */
export function formatSolution(solution: SolutionArtifact): string {
  const lines: string[] = [];

  lines.push("## Solution");
  lines.push("");

  lines.push(
    `**Summary:** ${escapeMarkdownInline(solution.data.one_line_summary ?? "")}`
  );
  lines.push("");

  if (solution.data.steps.length > 0) {
    lines.push("### Steps to implement");
    lines.push("");
    for (let i = 0; i < solution.data.steps.length; i++) {
      const step = solution.data.steps[i];
      if (step) {
        lines.push(`${i + 1}. **${escapeMarkdownInline(step.title)}**`);
        lines.push("");
        // step.description may contain markdown — pass it through as-is
        lines.push(`   ${step.description.split("\n").join("\n   ")}`);
        lines.push("");
      }
    }
  }

  return renderMarkdown(lines.join("\n"));
}
